import React, { useMemo, useRef } from 'react';
import isEqual from 'react-fast-compare';
import keyBy from 'lodash/keyBy';
import { useDebugRerenders } from '@wix/da-hooks/pkg/useDebugRerenders';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';
import type { TorpedoProps } from '../types';
import { useTorpedoLayout } from './hooks/useTorpedoLayout';
import { didItemsChange } from './helpers';
import TorpedoRow from './_partials/TorpedoRow';
import s from './BaseTorpedo.scss';

const BaseTorpedo: React.FC<TorpedoProps> = props => {
  const {
    innerRef,
    items = [],
    container,
    layoutConfig,
    useCardLayout,
    maxRows,
    hideIncompleteRows,
    enableScrollVisibilityOptimisation,
    contentStrip,
    contentStripOffset = 0,
    renderItem,
    onOffsetReached,
    testRenderSpy,
    testRecalculateLayoutSpy,
    rowAlign,
  } = props;
  const { rows, styleParams } = useTorpedoLayout({
    items,
    container,
    layoutConfig,
    useCardLayout,
    maxRows,
    hideIncompleteRows,
    testRecalculateLayoutSpy,
  });
  useDebugRerenders('BaseTorpedo', { ...props, rows, styleParams });
  const dataItemDictionary = useMemo(
    () => keyBy(items, item => item.id),
    [items]
  );

  // TODO: debugging, remove
  const defaultRef = useRef<HTMLDivElement>(null);
  const ref = innerRef ?? defaultRef;

  if (!rows?.length) {
    return (
      <ErrorBoundary debugComponent="BaseTorpedo">
        <div className={s['root']} ref={innerRef} />
      </ErrorBoundary>
    );
  }

  // Remove the outer gutter, this works by root having
  // overflow:hidden and width set to container.width
  const wrapMargins: React.CSSProperties = {
    marginLeft: `-${styleParams.itemSpacing}px`,
    marginTop: `-${styleParams.itemSpacing}px`,
    marginBottom: `-${styleParams.itemSpacing}px`,
  };

  testRenderSpy && testRenderSpy();
  return (
    <ErrorBoundary debugComponent="BaseTorpedo">
      <div
        className={s['root']}
        // ref={innerRef}
        ref={ref}
        style={{ width: container.width }}
      >
        <div style={wrapMargins}>
          {rows.map(row => (
            <TorpedoRow
              key={row.idx}
              idx={row.idx}
              width={row.width}
              height={row.height + (row.extraItemHeight ?? 0)}
              layoutItems={row.items}
              dataItems={dataItemDictionary}
              contentStrip={
                (row.idx === contentStripOffset + 1 ||
                  (rows.length <= contentStripOffset &&
                    row.idx === rows.length)) &&
                contentStrip
              }
              scrollOptims={enableScrollVisibilityOptimisation}
              onOffsetReached={onOffsetReached}
              renderItem={renderItem}
              rowAlign={rowAlign}
            />
          ))}
        </div>
      </div>
    </ErrorBoundary>
  );
};
BaseTorpedo.displayName = 'BaseTorpedo';

export default React.memo(
  BaseTorpedo,
  ({ items: prevItems, ...prevProps }, { items: nextItems, ...nextProps }) => {
    if (didItemsChange(prevItems, nextItems)) {
      return false;
    }

    return isEqual(prevProps, nextProps);
  }
);
